import { useState, useEffect } from 'react';

export const usePWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isIOSSafari, setIsIOSSafari] = useState(false);
  const [showIOSInstallPrompt, setShowIOSInstallPrompt] = useState(false);

  useEffect(() => {
    // Detect mobile device and browser
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
      
      const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      setIsIOS(ios);
      
      // Detect if it's Safari on iOS (needs manual instructions)
      // Chrome on iOS will have 'CriOS' in user agent, Safari won't
      const isChrome = /CriOS/.test(navigator.userAgent);
      const isFirefox = /FxiOS/.test(navigator.userAgent);
      const isEdge = /EdgiOS/.test(navigator.userAgent);
      
      // Only show manual instructions for Safari on iOS
      const isSafariOnIOS = ios && !isChrome && !isFirefox && !isEdge;
      setIsIOSSafari(isSafariOnIOS);
    };

    // Check if app is already installed
    const checkInstalled = () => {
      // Check if running in standalone mode (installed)
      const standalone = window.matchMedia('(display-mode: standalone)').matches;
      const fullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
      const navigatorStandalone = window.navigator.standalone === true;
      
      setIsInstalled(standalone || fullscreen || navigatorStandalone);
    };

    // Handle beforeinstallprompt event (Chrome/Edge)
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setIsInstallable(true);
    };

    // Handle app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    checkMobile();
    checkInstalled();

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Show install prompt
  const showInstallPrompt = async () => {
    if (!deferredPrompt) {
      return false;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstallable(false);
        setDeferredPrompt(null);
        return true;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  };

  // Show iOS Safari install instructions
  const showIOSInstructions = () => {
    setShowIOSInstallPrompt(true);
  };

  // Hide iOS Safari install instructions
  const hideIOSInstructions = () => {
    setShowIOSInstallPrompt(false);
    // Store in localStorage to not show again for a while
    localStorage.setItem('ios-install-prompt-dismissed', Date.now().toString());
  };

  // Check if iOS Safari prompt should be shown
  const shouldShowIOSPrompt = () => {
    if (!isIOSSafari || isInstalled) return false;
    
    const dismissed = localStorage.getItem('ios-install-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      return daysSinceDismissed > 7; // Show again after 7 days
    }
    
    return true;
  };

  return {
    isInstallable,
    isInstalled,
    isMobile,
    isIOS,
    isIOSSafari,
    showIOSInstallPrompt,
    shouldShowIOSPrompt: shouldShowIOSPrompt(),
    showInstallPrompt,
    showIOSInstructions,
    hideIOSInstructions,
    canInstall: isInstallable || (isIOSSafari && !isInstalled) || (isIOS && !isIOSSafari && !isInstalled)
  };
}; 